import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  Dropdown,
  DropdownButton,
  OverlayTrigger,
  Spinner,
  Tooltip,
} from "react-bootstrap";
import TopLimitDropdown from "../../../../../common/TopLimitDropdown";
import AccordionComponent from "../../../../../common/Accordion";
import { useDispatch } from "react-redux";
import {
  setIsDashboard,
  setNewCaseInFoID,
} from "../../../../../redux/slice/CaseHeaderSlice";
import { getRORRecentActivity } from "../../../../../services/RORDashboardAPI";
import { formattedDate } from "../../../../../utils/dateUtils";
import {
  HISTORY_REPORT_STATUS,
  ROR_STATUS,
  WEB_ROLE,
} from "../../../../../common/Constant";
import { getHistorianRecentActivity } from "../../../../../services/HistorianDashboardAPI";
import { getEditorRecentActivity } from "../../../../../services/EditorDashboardAPI";

interface RORRecentActivityProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
  setShowRushNotesSummary: any;
  userRole?: string;
}

const RORRecentActivity: React.FC<RORRecentActivityProps> = ({
  openDropdown,
  setOpenDropdown,
  setShowRushNotesSummary,
  userRole,
}) => {
  const dispatch = useDispatch();
  const [limit, setLimit] = useState<number>(5);
  const [limitLabel, setLimitLabel] = useState<string>("Top 5");
  const [caseData, setCaseData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [visibleHeaderIndex, setVisibleHeaderIndex] = useState<boolean>(false);

  const fetchLatestCases = async (limitValue: number) => {
    setLoading(true);

    try {
      let data;

      switch (userRole) {
        case WEB_ROLE.Record_Reviewer:
          data = await getRORRecentActivity(limitValue);
          break;

        case WEB_ROLE.Historian:
          data = await getHistorianRecentActivity(limitValue);
          break;

        case WEB_ROLE.Editor:
          data = await getEditorRecentActivity(limitValue);
          break;

        default:
          data = [];
      }

      setCaseData(data);
    } catch (error) {
      console.error("Failed to fetch latest cases:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatestCases(limit);
  }, [limit]);

  const handleLimitChange = (value: number, label: string) => {
    setLimit(value);
    setLimitLabel(label);
  };

  const handleOpenRushNotes = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLimit(5);
    setLimitLabel("Top 5");
    setShowRushNotesSummary(true);
  };

  const [LatestCaseKey, setLatestCaseKey] = useState<string | null>("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    // Prevent collapsing if the same key is clicked
    if (LatestCaseKey === key) {
      return; // do nothing
    }
    setLatestCaseKey(key || "0");
  };

  const LatestCaseAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={LatestCaseKey}
      onSelect={handleBodyAccordionSelect}
    />
  );

  const TruncatedWithTooltip = ({ text }: { text: string }) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
      const el = textRef.current;
      if (el) {
        setIsTruncated(el.scrollWidth > el.clientWidth);
      }
    }, [text]);

    const content = (
      <span
        ref={textRef}
        className="fw-semibold text-truncate d-inline-block"
        style={{ maxWidth: "100%" }}
      >
        {text}
      </span>
    );

    return isTruncated ? (
      <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
        {content}
      </OverlayTrigger>
    ) : (
      content
    );
  };

  const ROR_STATUS_COLORS: Record<string, string> = {
    [ROR_STATUS.NOT_INITIATED]: "#6c757d",
    [ROR_STATUS.IN_REVIEW]: "#0d6efd",
    [ROR_STATUS.DRAFT_REVIEW]: "#fd7e14",
    [ROR_STATUS.REVIEW_COMPLETED]: "#198754",
    [ROR_STATUS.Review_Completed_Delayed]: "#dc3545",
    [ROR_STATUS.EXCEPTION]: "#842029",
    [ROR_STATUS.Cancelled_Appointment]: "#574949",
    [HISTORY_REPORT_STATUS.NOT_SCHEDULED]: "#6c757d",
    [HISTORY_REPORT_STATUS.PENDING]: "#0d6efd",
    [HISTORY_REPORT_STATUS.DRAFT]: "#fd7e14",
    [HISTORY_REPORT_STATUS.EXCEPTION]: "#842029",
    [HISTORY_REPORT_STATUS.COMPLETED]: "#198754",
  };

  return (
    <>
      {LatestCaseAccordion([
        {
          title: (
            <div className="d-flex align-items-center w-100">
              {/* Left side title block */}
              <div className="me-auto d-flex flex-column">
                <span className="fw-semibold">Recent Activity</span>
                <span
                  style={{
                    fontSize: "12px",
                    fontStyle: "italic",
                    color: "#6c757d",
                  }}
                >
                  Most Recent Case Movements
                </span>
              </div>

              {/* Dropdown */}
              <div onClick={(e) => e.stopPropagation()}>
                <TopLimitDropdown
                  title={limitLabel}
                  onSelect={handleLimitChange}
                  show={openDropdown === "ror_recentActivity"}
                  onToggle={(isOpen) =>
                    setOpenDropdown(isOpen ? "ror_recentActivity" : null)
                  }
                />
              </div>

              {/* Icon */}
              <span className="rushIcon rushiconDashboard">
                <span
                  className="material-symbols-outlined me-2 rushicon p-1 heartbeat"
                  onClick={handleOpenRushNotes}
                >
                  sprint
                </span>
              </span>
            </div>
          ),
          content: (
            <>
              <Card className="border-0 ">
                <Card.Body className="d-flex flex-column p-0 bg-theme">
                  <div
                    style={{
                      maxHeight: limit > 5 ? "250px" : "auto",
                      overflowY: "auto",
                    }}
                    className="d-flex flex-column gap-1 flex-grow-1"
                  >
                    {caseData.length === 0 ? (
                      <p>No Recent Activity.</p>
                    ) : (
                      caseData.map((item, index) => (
                        <div
                          key={index}
                          className="latest-case py-1"
                          style={{
                            borderBottom: "1px solid #e9ecef",
                            fontSize: "13px",
                            lineHeight: "1.2",
                          }}
                        >
                          <div className="d-flex justify-content-between">
                            {/* Left Column */}
                            <div
                              className="d-flex flex-column text-truncate"
                              style={{ maxWidth: "65%" }}
                            >
                              {userRole == WEB_ROLE.Record_Reviewer && (
                                <TruncatedWithTooltip text={item.rorName} />
                              )}
                              {userRole == WEB_ROLE.Historian && (
                                <TruncatedWithTooltip
                                  text={item.historianName}
                                />
                              )}
                              {userRole == WEB_ROLE.Editor && (
                                <TruncatedWithTooltip text={item.userName} />
                              )}

                              <span className="text-muted text-truncate mt-1">
                                <a
                                  href="#/search-case"
                                  className="text-blue-600 underline dashbardCaseAnchr"
                                  onClick={(e) => {
                                    dispatch(setIsDashboard(true));
                                    dispatch(setNewCaseInFoID(item.caseInfoId));
                                    setVisibleHeaderIndex(true);
                                  }}
                                >
                                  {item.caseId || "-"}
                                </a>
                              </span>
                            </div>

                            {/* Right Column */}
                            {userRole == WEB_ROLE.Record_Reviewer && (
                              <div className="d-flex flex-column text-end">
                                <span className="text-muted">
                                  {formattedDate(item.activityDate)}
                                </span>
                                <span
                                  style={{
                                    fontWeight: 500,
                                    color:
                                      ROR_STATUS_COLORS[item.rorStatus] ||
                                      "#000",
                                  }}
                                >
                                  {item.rorStatus}
                                </span>
                              </div>
                            )}
                            {userRole == WEB_ROLE.Historian && (
                              <div className="d-flex flex-column text-end">
                                <span className="text-muted">
                                  {formattedDate(item.activityDate)}
                                </span>
                                <span
                                  style={{
                                    fontWeight: 500,
                                    color:
                                      ROR_STATUS_COLORS[item.historyStatus] ||
                                      "#000",
                                  }}
                                >
                                  {item.historyStatus}
                                </span>
                              </div>
                            )}
                            {userRole == WEB_ROLE.Editor && (
                              <div className="d-flex flex-column text-end">
                                <span className="text-muted">
                                  {formattedDate(item.activityDate)}
                                </span>
                                <span
                                  style={{
                                    fontWeight: 500,
                                    color:
                                      ROR_STATUS_COLORS[item.status] || "#000",
                                  }}
                                >
                                  {item.status}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </Card.Body>
              </Card>
            </>
          ),
        },
      ])}
    </>
  );
};

export default RORRecentActivity;
